import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Loader2, Save, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatApiError } from '@/components/ProtectedRoute';
import { PageMeta } from '@/components/PageMeta';
import { API } from '@/lib/api';


const EMPTY = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  cover_image: '',
  author: 'Nicolas Perroy',
  tags: '',
  meta_description: '',
  published: true,
};

export default function AdminBlogEditorPage() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        // Use admin list to find the post (includes unpublished)
        const { data } = await axios.get(`${API}/blog/admin/posts`);
        const post = data.find((p) => p.slug === slug);
        if (!post) {
          toast.error('Article introuvable');
          navigate('/admin/blog');
          return;
        }
        setForm({
          title: post.title || '',
          slug: post.slug || '',
          excerpt: post.excerpt || '',
          content: post.content || '',
          cover_image: post.cover_image || '',
          author: post.author || 'Nicolas Perroy',
          tags: (post.tags || []).join(', '),
          meta_description: post.meta_description || '',
          published: post.published ?? true,
        });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isEdit, navigate]);

  const update = (key) => (e) => setForm({ ...form, [key]: e?.target ? e.target.value : e });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image: form.cover_image.trim() || null,
      author: form.author.trim() || 'Nicolas Perroy',
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      meta_description: form.meta_description.trim() || null,
      published: form.published,
    };
    try {
      if (isEdit) {
        await axios.patch(`${API}/blog/admin/posts/${slug}`, payload);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/blog/admin/posts`, payload);
        toast.success('Article créé');
      }
      navigate('/admin/blog');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageMeta title={isEdit ? 'Modifier article' : 'Nouvel article'} description="Éditeur blog" />
      <div className="mb-6">
        <Link to="/admin/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4" /> Retour à la liste
        </Link>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ {isEdit ? 'Modifier' : 'Créer'}</span>
            <h1 className="font-heading text-4xl text-dark mt-2">
              {isEdit ? 'ÉDITEUR D\'ARTICLE' : 'NOUVEL ARTICLE'}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" type="button" onClick={() => setPreview(!preview)} className="rounded-xl" data-testid="toggle-preview">
              <Eye className="h-4 w-4 mr-2" /> {preview ? 'Masquer aperçu' : 'Aperçu'}
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className={`grid ${preview ? 'lg:grid-cols-2' : ''} gap-6`}>
          <Card className="p-6 rounded-2xl">
            <div className="space-y-5">
              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Titre *</Label>
                <Input required value={form.title} onChange={update('title')} className="h-11 rounded-xl" data-testid="blog-editor-title" placeholder="Comment progresser en CrossFit ?" />
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">
                  Slug URL <span className="normal-case tracking-normal text-[10px] ml-2">(optionnel, généré depuis le titre)</span>
                </Label>
                <Input value={form.slug} onChange={update('slug')} className="h-11 rounded-xl font-mono text-sm" placeholder="comment-progresser-crossfit" />
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Image de couverture (URL)</Label>
                <Input value={form.cover_image} onChange={update('cover_image')} className="h-11 rounded-xl" placeholder="https://..." />
                {form.cover_image && (
                  <img loading="lazy" decoding="async" src={form.cover_image} alt="" className="mt-3 w-full h-40 object-cover rounded-xl" />
                )}
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Extrait (meta description) *</Label>
                <Textarea required value={form.excerpt} onChange={update('excerpt')} rows={3} className="rounded-xl" data-testid="blog-editor-excerpt" placeholder="Un résumé de 2-3 phrases qui sera affiché dans la liste et dans Google." />
                <div className="text-[10px] text-muted-foreground mt-1">{form.excerpt.length}/500 caractères — idéal : 150-160 pour le SEO</div>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Tags (séparés par des virgules)</Label>
                <Input value={form.tags} onChange={update('tags')} className="h-11 rounded-xl" placeholder="CrossFit, Débutant, Technique" />
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Meta description SEO (optionnel)</Label>
                <Textarea value={form.meta_description} onChange={update('meta_description')} rows={2} className="rounded-xl" placeholder="Description affichée dans les résultats Google (si différente de l'extrait)" />
              </div>

              <div>
                <Label className="mb-1.5 block text-xs tracking-widest uppercase text-muted-foreground">Contenu (Markdown supporté) *</Label>
                <Textarea
                  required
                  value={form.content}
                  onChange={update('content')}
                  rows={20}
                  className="rounded-xl font-mono text-sm leading-relaxed"
                  data-testid="blog-editor-content"
                  placeholder="# Mon titre&#10;&#10;Paragraphe de texte...&#10;&#10;## Sous-titre&#10;&#10;- Liste&#10;- Élément&#10;&#10;**Gras** et *italique*"
                />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Markdown : # = H1, ## = H2, **gras**, *italique*, - liste, [texte](url) = lien
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-accent rounded-xl">
                <div>
                  <Label className="text-sm font-semibold text-dark">Publier l'article</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.published ? 'Visible sur le site public' : 'Brouillon — non visible'}
                  </p>
                </div>
                <Switch checked={form.published} onCheckedChange={update('published')} data-testid="blog-editor-published" />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={saving} className="btn-shine bg-primary hover:bg-primary-light rounded-xl flex-1" data-testid="blog-editor-save">
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" /> {isEdit ? 'Mettre à jour' : 'Créer l\'article'}</>
                  )}
                </Button>
              </div>
            </div>
          </Card>

          {preview && (
            <Card className="p-6 rounded-2xl bg-background max-h-[80vh] overflow-y-auto sticky top-4">
              <div className="text-xs tracking-widest uppercase text-primary mb-3">━━ Aperçu</div>
              {form.cover_image && <img loading="lazy" decoding="async" src={form.cover_image} alt="" className="w-full h-48 object-cover rounded-xl mb-5" />}
              <h1 className="font-heading text-3xl text-dark mb-3 leading-tight">{form.title || 'Titre de l\'article'}</h1>
              <p className="text-muted-foreground mb-6 italic">{form.excerpt}</p>
              <div className="prose prose-sm max-w-none prose-headings:font-heading prose-headings:tracking-wide prose-a:text-primary prose-strong:text-dark">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || '*Commence à écrire ton article...*'}</ReactMarkdown>
              </div>
            </Card>
          )}
        </div>
      </form>
    </AdminLayout>
  );
}
